import { gql } from '@apollo/client';
import React from 'react';

import { Loader } from '@sbu/components.loader';
import {
  allFeatureFlags,
  enabledByDefaultFeatureFlags,
} from '@sbu/hooks.featureflag';

import {
  AppInfoOperationQuery as AppInfoOperation,
  AppInfoOperationSumoQuery,
  AppInfoOperationSumoQueryVariables,
  AppInfoOperationQueryVariables as AppInfoOperationVariables,
} from 'core/graphql';
import { useQuery } from 'shared/hooks';

export const APP_INFO_QUERY = gql`
  fragment CurrentUserFragment on CurrentUser {
    createdTime
    email
    id
    name
    permissions
    preferredTimezone
    username
  }
  query AppInfoOperation(
    $featureFlagsToLookup: [String!]!
    $enabledByDefaultFeatureFlags: [String!]!
  ) {
    currentUser {
      ...CurrentUserFragment
    }
    isFederatedCluster
    enabledFeatureFlags(
      featureFlagsToLookup: $featureFlagsToLookup
      enabledByDefaultFeatureFlags: $enabledByDefaultFeatureFlags
    )
    version
    sumoLogicRegion
    deployment
    orgId
    orgName
    cspCustomReportUrl
    sessionTimeoutMinutes
  }
`;

export const APP_INFO_CURRENT_USER_SUMO_QUERY = gql`
  fragment CurrentUserSumoFragment on CurrentUserSumo {
    id
    accountType
    accountStatus
    organization
    organizationId
    userHashForIntercom
    userIdForIntercom
  }
  query AppInfoOperationSumo {
    currentUserSumo {
      ...CurrentUserSumoFragment
    }
  }
`;

export type AppInfo = AppInfoOperation & {
  currentUserSumo: AppInfoOperationSumoQuery['currentUserSumo'];
};

type AppInfoQueryProps = {
  showLoader?: boolean;
  includeCurrentUserSumo?: boolean;
  children: (appInfo: AppInfo) => JSX.Element | null;
};

export const useAppInfoQuery = (includeCurrentUserSumo: boolean = false) => {
  const { data, loading, error } = useQuery<
    AppInfoOperation,
    AppInfoOperationVariables
  >(APP_INFO_QUERY, {
    notifyErrors: (error) => {
      const [graphQLError] = error?.graphQLErrors ?? [];
      return graphQLError?.message !== 'Request not authorized';
    },
    variables: {
      featureFlagsToLookup: allFeatureFlags,
      enabledByDefaultFeatureFlags: enabledByDefaultFeatureFlags,
    },
  });
  const { data: currentUserSumo, loading: currentUserSumoLoading } = useQuery<
    AppInfoOperationSumoQuery,
    AppInfoOperationSumoQueryVariables
  >(APP_INFO_CURRENT_USER_SUMO_QUERY, {
    notifyErrors: (error) => {
      const [graphQLError] = error?.graphQLErrors ?? [];
      return graphQLError?.message !== 'Request not authorized';
    },
    skip: !includeCurrentUserSumo,
    context: {
      targetGraph: 'AURORA',
      enableSplitHttpLink: false,
    },
  });

  return error
    ? { error }
    : {
        data: data
          ? ({
              ...data,
              currentUserSumo: currentUserSumo?.currentUserSumo,
            } as AppInfo)
          : undefined,
        loading: loading || currentUserSumoLoading,
      };
};

export const AppInfoQuery = ({
  showLoader,
  includeCurrentUserSumo = false,
  children,
}: AppInfoQueryProps) => {
  const { data, loading } = useAppInfoQuery(includeCurrentUserSumo);

  if (loading && showLoader) {
    return <Loader height='100%' size='xlarge' />;
  }

  if (!loading && data) {
    return children(data);
  }

  return null;
};
